import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import theme from '@/theme';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import PublicLayout from './components/PublicLayout';
import Chatbot from './components/Chatbot';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SuperAdminDashboard from './pages/SuperAdmin/Dashboard';
import Schools from './pages/SuperAdmin/Schools';
import SchoolForm from './pages/SuperAdmin/SchoolForm';
import SchoolDetail from './pages/SuperAdmin/SchoolDetail';
import Analytics from './pages/SuperAdmin/Analytics';
import FeatureManagement from './pages/SuperAdmin/FeatureManagement';
import Subscriptions from './pages/SuperAdmin/Subscriptions';
import ApiCosts from './pages/SuperAdmin/ApiCosts';
import AuditLog from './pages/SuperAdmin/AuditLog';
import PrincipalDashboard from './pages/Principal/Dashboard';
import PrincipalAnalytics from './pages/Principal/Analytics';
import Teachers from './pages/Principal/Teachers';
import AddTeacher from './pages/Principal/AddTeacher';
import TeacherWorkHours from './pages/Principal/TeacherWorkHours';
import Students from './pages/Principal/Students';
import AddStudent from './pages/Principal/AddStudent';
import Classes from './pages/Principal/Classes';
import AddClass from './pages/Principal/AddClass';
import Attendance from './pages/Principal/Attendance';
import ParentMeetings from './pages/Principal/ParentMeetings';
import NonTeachingStaff from './pages/Principal/NonTeachingStaff';
import AddNonTeachingStaff from './pages/Principal/AddNonTeachingStaff';
import TeacherDashboard from './pages/Teacher/Dashboard';
import TeacherAnalytics from './pages/Teacher/Analytics';
import TeacherClasses from './pages/Teacher/Classes';
import ClassManagement from './pages/Teacher/ClassManagement';
import AddSubject from './pages/Teacher/AddSubject';
import EnterMarks from './pages/Teacher/EnterMarks';
import AddWeeklyPlan from './pages/Teacher/AddWeeklyPlan';
import MarkAttendance from './pages/Teacher/MarkAttendance';
import ExamSchedule from './pages/Teacher/ExamSchedule';
import ManageParentMeetings from './pages/Teacher/ManageParentMeetings';
import WeeklyReports from './pages/Principal/WeeklyReports';
import ManageExams from './pages/Principal/ManageExams';
import ViewMarks from './pages/Principal/ViewMarks';
import SchoolAdmins from './pages/Principal/SchoolAdmins';
import AddSchoolAdmin from './pages/Principal/AddSchoolAdmin';
import ManageLeaves from './pages/Principal/ManageLeaves';
import HolidayCalendar from './pages/Principal/HolidayCalendar';
import PrincipalAnnouncements from './pages/Principal/Announcements';
import AcademicCalendarPage from './pages/Principal/AcademicCalendar';
import ClassTimetable from './pages/Principal/ClassTimetable';
import LeaveManagement from './pages/Teacher/LeaveManagement';
import TeacherHolidayCalendar from './pages/Teacher/HolidayCalendar';
import TeacherAnnouncements from './pages/Teacher/Announcements';
import MySchedule from './pages/Teacher/MySchedule';
import Notifications from './pages/Notifications';
import ParentDashboard from './pages/Parent/Dashboard';
import ParentChildDetails from './pages/Parent/ChildDetails';
import ParentMeetingsPage from './pages/Parent/Meetings';
import ParentCareNotes from './pages/Parent/CareNotes';
import TeacherCareNotes from './pages/Teacher/CareNotes';

import TeacherExpenses from './pages/Teacher/Expenses';
import ParentChildDocuments from './pages/Parent/ChildDocuments';
import Terms from './pages/Terms';
import PrincipalStudentDocuments from './pages/Principal/StudentDocuments';
import RequiredDocuments from './pages/Principal/RequiredDocuments';
import DaycareManagement from './pages/Principal/DaycareManagement';
import DaycareStudentProfile from './pages/Principal/DaycareStudentProfile';
import DaycareRoster from './pages/Teacher/DaycareRoster';
import PostDailyUpdate from './pages/Teacher/PostDailyUpdate';
import ParentQueries from './pages/Teacher/ParentQueries';
import MyQueries from './pages/Parent/MyQueries';
import PrincipalHelpGuide from './pages/Principal/HelpGuide';
import TeacherHelpGuide from './pages/Teacher/HelpGuide';
import ParentHelpGuide from './pages/Parent/HelpGuide';

// Lazy load finance components for better performance (large components)
const ManageFinance = lazy(() => import('./pages/Principal/ManageFinance'));
const DaycareDues = lazy(() => import('./pages/Principal/DaycareDues'));
const EnterPayment = lazy(() => import('./pages/Principal/EnterPayment'));
const ManageReportCards = lazy(() => import('./pages/Principal/ManageReportCards'));
const ReportCardViewer = lazy(() => import('./pages/Principal/ReportCardViewer'));
const Inventory = lazy(() => import('./pages/Principal/Inventory'));
const VisitorLog = lazy(() => import('./pages/Principal/VisitorLog'));
const Library = lazy(() => import('./pages/Principal/Library'));
const TeacherLibrary = lazy(() => import('./pages/Teacher/Library'));
const DataExport = lazy(() => import('./pages/Principal/DataExport'));
const ManageExpenses = lazy(() => import('./pages/Principal/ManageExpenses'));
const PrincipalInbox = lazy(() => import('./pages/Principal/Inbox'));
const TeacherInbox = lazy(() => import('./pages/Teacher/Inbox'));
const PrincipalStaffCalendar = lazy(() => import('./pages/Principal/StaffCalendar'));
const TeacherStaffCalendar = lazy(() => import('./pages/Teacher/StaffCalendar'));
const TeacherCalendar = lazy(() => import('./pages/Teacher/Calendar'));
const ParentCalendar = lazy(() => import('./pages/Parent/Calendar'));

// Loading fallback component
const PageLoader = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="80vh"
  >
    <CircularProgress size={60} />
  </Box>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
            <Route path="/forgot-password" element={<PublicLayout><ForgotPassword /></PublicLayout>} />
            <Route path="/reset-password" element={<PublicLayout><ResetPassword /></PublicLayout>} />
            <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />

            {/* Super Admin Routes */}
            <Route
              path="/superadmin/dashboard"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/schools"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <Schools />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/schools/new"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <SchoolForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/schools/:id"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <SchoolDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/schools/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <SchoolForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/analytics"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/features"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <FeatureManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/subscriptions"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <Subscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/costs"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <ApiCosts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/audit-log"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AuditLog />
                </ProtectedRoute>
              }
            />

            {/* Principal Routes - You can add more routes here */}
            <Route
              path="/principal/dashboard"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <PrincipalDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/teachers"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Teachers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/teachers/new"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <AddTeacher />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/teachers/edit/:id"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <AddTeacher />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/teachers/work-hours"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <TeacherWorkHours />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/leave-management"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <ManageLeaves />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/holidays"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <HolidayCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/calendar"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <AcademicCalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/timetable"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <ClassTimetable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/announcements"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <PrincipalAnnouncements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/students"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Students />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/students/new"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <AddStudent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/students/edit/:id"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <AddStudent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/classes"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Classes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/classes/new"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <AddClass />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/classes/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <AddClass />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/attendance"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <Attendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/weekly-reports"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <WeeklyReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/exams"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <ManageExams />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/view-marks"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <ViewMarks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/finance"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Suspense fallback={<PageLoader />}>
                    <ManageFinance />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/finance/enter-payment"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Suspense fallback={<PageLoader />}>
                    <EnterPayment />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/daycare-dues"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Suspense fallback={<PageLoader />}>
                    <DaycareDues />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/parent-meetings"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <ParentMeetings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/non-teaching-staff"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <NonTeachingStaff />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/non-teaching-staff/new"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <AddNonTeachingStaff />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/non-teaching-staff/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <AddNonTeachingStaff />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/analytics"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <PrincipalAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/report-cards"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <Suspense fallback={<PageLoader />}>
                    <ManageReportCards />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/report-cards/view"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <Suspense fallback={<PageLoader />}>
                    <ReportCardViewer />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="/principal/inventory"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Suspense fallback={<PageLoader />}>
                    <Inventory />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/visitor-log"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Suspense fallback={<PageLoader />}>
                    <VisitorLog />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/library"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Suspense fallback={<PageLoader />}>
                    <Library />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/expenses"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <Suspense fallback={<PageLoader />}>
                    <ManageExpenses />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/data-export"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <Suspense fallback={<PageLoader />}>
                    <DataExport />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/students/:studentId/documents"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <PrincipalStudentDocuments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/required-documents"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <RequiredDocuments />
                </ProtectedRoute>
              }
            />

            {/* School Admin Management Routes (Principal only) */}
            <Route
              path="/principal/school-admins"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <SchoolAdmins />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/school-admins/new"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <AddSchoolAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/school-admins/edit/:id"
              element={
                <ProtectedRoute allowedRoles={['principal']}>
                  <AddSchoolAdmin />
                </ProtectedRoute>
              }
            />

            {/* Teacher Routes */}
            <Route
              path="/teacher/dashboard"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <TeacherDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/classes"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <TeacherClasses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/classes/:id/manage"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <ClassManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/classes/:id/subjects"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <AddSubject />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/classes/:id/marks"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <EnterMarks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/classes/:id/weekly-plan"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <AddWeeklyPlan />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/attendance"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <MarkAttendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/classes/:id/exam-schedule"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <ExamSchedule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/meetings"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <ManageParentMeetings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/analytics"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <TeacherAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/leave-management"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <LeaveManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/holidays"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <TeacherHolidayCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/my-schedule"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <MySchedule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/calendar"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <Suspense fallback={<PageLoader />}>
                    <TeacherCalendar />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/announcements"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <TeacherAnnouncements />
                </ProtectedRoute>
              }
            />

            {/* Notification Routes */}
            <Route
              path="/principal/notifications"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/notifications"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/notifications"
              element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <Notifications />
                </ProtectedRoute>
              }
            />

            {/* Parent Routes */}
            <Route
              path="/parent/dashboard"
              element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <ParentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/child/:studentId"
              element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <ParentChildDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/meetings"
              element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <ParentMeetingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/care-notes"
              element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <ParentCareNotes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/calendar"
              element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <Suspense fallback={<PageLoader />}>
                    <ParentCalendar />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/children/:studentId/documents"
              element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <ParentChildDocuments />
                </ProtectedRoute>
              }
            />

            {/* Teacher Care Notes */}
            <Route
              path="/teacher/care-notes"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <TeacherCareNotes />
                </ProtectedRoute>
              }
            />

            {/* Teacher Expenses */}
            <Route
              path="/teacher/expenses"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <TeacherExpenses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/library"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <Suspense fallback={<PageLoader />}>
                    <TeacherLibrary />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Daycare */}
            <Route
              path="/principal/daycare"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <DaycareManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/principal/daycare/students/:studentId"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <DaycareStudentProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/daycare"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <DaycareRoster />
                </ProtectedRoute>
              }
            />

            {/* Staff Inbox */}
            <Route
              path="/principal/inbox"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Suspense fallback={<PageLoader />}>
                    <PrincipalInbox />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/inbox"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <Suspense fallback={<PageLoader />}>
                    <TeacherInbox />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Staff Calendar */}
            <Route
              path="/principal/staff-calendar"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <Suspense fallback={<PageLoader />}>
                    <PrincipalStaffCalendar />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/staff-calendar"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <Suspense fallback={<PageLoader />}>
                    <TeacherStaffCalendar />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Daily Updates */}
            <Route
              path="/teacher/daily-updates"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <PostDailyUpdate />
                </ProtectedRoute>
              }
            />

            {/* Parent Queries */}
            <Route
              path="/teacher/parent-queries"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <ParentQueries />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/queries"
              element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <MyQueries />
                </ProtectedRoute>
              }
            />

            {/* Help / User Guides */}
            <Route
              path="/principal/help-guide"
              element={
                <ProtectedRoute allowedRoles={['principal', 'school_admin']}>
                  <PrincipalHelpGuide />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/help-guide"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <TeacherHelpGuide />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/help-guide"
              element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <ParentHelpGuide />
                </ProtectedRoute>
              }
            />

            {/* Default Route */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            
            {/* 404 Route */}
            <Route path="*" element={<div>404 - Page Not Found</div>} />
          </Routes>
          <Chatbot />
        </Router>

        {/* Toast Notifications */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />

      </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
